import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GetChatsPageResponse,
  GetGroupReportTopicsResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import classNames from './TutorReportModal.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { Transition } from 'react-transition-group';
import { interfaceLanguageSelector, authSelector } from '@/store/reducers/auth';
import { useAppSelector } from '@/store';
import SVG from 'react-inlinesvg';
import AppBackdrop from '@/components/AppBackdrop';
import AppModal from '@/components/AppModal';
import closeIcon from '@/assets/svg/closeFill.svg';
import leftArrow from '@/assets/svg/left-arrow.svg';
import { ClassOverviewData } from '@/types/progress';
import { TeacherGroup } from '@/types/user';
import { UserStatusEnum } from '@/types/register';
import commonUtils from '@/utils/common';
import UserUtils from '@/utils/user';
import TutorUserReport from './TutorUserReport';
import TutorTopicReport from './TutorTopicReport';

interface TutorReportModalProps {
  show?: boolean;
  onClose?: () => void;
  report: GetChatsPageResponse | GetGroupReportTopicsResponse[] | null;
  reportUserId: string;
  group: TeacherGroup;
  studentsMapper: { [student_id: string]: ClassOverviewData };
}

const TutorReportModal = ({
  show,
  onClose,
  report,
  reportUserId,
  group,
  studentsMapper,
}: TutorReportModalProps) => {
  const { t } = useTranslation();
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const { user } = useAppSelector(authSelector);
  const [selectedStudent, setSelectedStudent] = useState<ClassOverviewData | null>(null);
  const [chatHistoryId, setChatHistoryId] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<GetChatsPageResponse | null>(null);
  const [userTopics, setUserTopics] = useState<GetGroupReportTopicsResponse[]>([]);
  const [rootView, setRootView] = useState<string>('');

  const isTeacher = useMemo(() => {
    return UserUtils.isTeacher(user);
  }, [user]);

  useEffect(() => {
    let isActive = true;

    if (!show || !user) {
      setTimeout(() => {
        if (isActive) {
          setChatHistoryId(null);
          setSelectedTopic(null);
        }
      }, 500);

      return () => {
        isActive = false;
      };
    }

    const isTopicReport = report && !('length' in report);

    setSelectedStudent(
      studentsMapper[reportUserId] || {
        id: user.id,
        level: user.progress?.userLevel || -3,
        firstName: user.metadata.firstName,
        lastName: user.metadata.lastName,
        avatar: user.metadata?.userSettings?.avatar,
      }
    );

    if (isTopicReport) {
      setRootView('topic');
      setSelectedTopic(report as GetChatsPageResponse);
      setUserTopics([]);
    } else {
      setRootView('user');
      setSelectedTopic(null);
      setUserTopics(report as GetGroupReportTopicsResponse[]);
    }

    return () => {
      isActive = false;
    };
  }, [show, report, reportUserId, group, studentsMapper, user]);

  const view = useMemo(() => {
    if (chatHistoryId) {
      return 'chat';
    } else if (selectedTopic) {
      return 'topic';
    } else if (selectedStudent) {
      return 'user';
    }
    return '';
  }, [selectedTopic, chatHistoryId, selectedStudent]);

  /*
  const startConversation = useCallback(() => {
    if (selectedTopic) {
      navigate('/dashboard/my-tutor-chat', {
        state: {
          type: PrivateTutorTypes.START_OVER,
          levelId: selectedTopic?.levelId || 0,
          unitId: selectedTopic?.unitId || 0,
          topicId: selectedTopic?.topicId || 0,
          activityId: selectedTopic?.activityId || 0,
        },
      });
    }
  }, [selectedTopic, navigate]);
  */

  const getUserAvatar = useCallback((student: ClassOverviewData) => {
    const avatarColor = student?.avatar?.avatarColor || '#3ad0f9';
    const avatarName = student?.avatar?.avatarName || '';

    return (
      <div className={classNames.avatar} style={{ background: avatarColor }}>
        {avatarName ? (
          <SVG src={commonUtils.getAvatarImage(avatarName) ?? ''} />
        ) : (
          <span>{commonUtils.getInitials(student?.firstName, student?.lastName)}</span>
        )}
      </div>
    );
  }, []);

  const goBack = useCallback(() => {
    if (view === 'chat') {
      setChatHistoryId(null);
    } else if (view === 'topic') {
      if (view === rootView) {
        onClose?.();
      } else {
        setSelectedTopic(null);
      }
    } else {
      onClose?.();
    }
  }, [view, rootView, onClose]);

  const startHead = useMemo(() => {
    return (
      <div className={classNames.startHead}>
        <div className={classNames.start}></div>
        <button className={classNames.backButton} onClick={goBack}>
          <SVG src={leftArrow} />
          <span>{t('back')}</span>
        </button>
      </div>
    );
  }, [t, selectedStudent, goBack]);

  const header = useMemo(() => {
    if (!selectedStudent) {
      return null;
    }

    const grade =
      isTeacher && group?.id
        ? group.grade
        : isTeacher
          ? ''
          : user?.metadata?.organizationDetails?.grade;
    const level =
      selectedStudent.level ||
      (selectedStudent.userStatus === UserStatusEnum.LEVEL_TEST_INCOMPLETE ? 0 : -1);

    const gradeText = `${t(`grade_${grade}`)} ${t('grade').toLowerCase()}`;
    const levelText =
      level === 0
        ? t('levelTestIncomplete')
        : level === -1
          ? t('inactive')
          : `${level} - ${t(`levelName_${level}`)}`;

    const groupName =
      isTeacher && group?.id
        ? group.name
        : isTeacher
          ? ''
          : user?.metadata?.organizationDetails?.groupName;

    return (
      <div className={classNames.profileContainer}>
        {getUserAvatar(selectedStudent)}

        <div className={classNames.userDetails}>
          <h2
            className={classNames.userName}
          >{`${selectedStudent.firstName} ${selectedStudent.lastName}`}</h2>

          <div className={classNames.userInfoContainer}>
            {(!isTeacher || group?.id) && (
              <>
                <span className={classNames.userInfo}>{gradeText}</span>
                <div className={classNames.divider} />
                <span className={classNames.userInfo}>{groupName}</span>
                <div className={classNames.divider} />
                <span className={classNames.userInfo}>{levelText}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }, [group, selectedStudent, t, getUserAvatar, isTeacher, user]);

  return (
    <Transition
      in={show}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <>
            <AppBackdrop
              className={classes(classNames.backdrop, `backdrop-${state}`)}
              emitClose={onClose}
            />
            <AppModal>
              <div
                className={classes(
                  classNames.modal,
                  `slide-horizontal-${interfaceLanguage?.direction}-${state}`
                )}
              >
                <button
                  className={classNames.closeBtn}
                  onClick={onClose}
                  aria-label={t('close').toString()}
                >
                  <SVG src={closeIcon} />
                </button>

                {view === 'user' && (
                  <TutorUserReport
                    startHead={startHead}
                    header={header}
                    userTopics={userTopics}
                    setSelectedTopic={setSelectedTopic}
                  />
                )}

                {(view === 'topic' || view === 'chat') && selectedTopic && selectedStudent && (
                  <TutorTopicReport
                    startHead={startHead}
                    header={header}
                    selectedTopic={selectedTopic}
                    selectedStudent={selectedStudent}
                    chatHistoryId={chatHistoryId}
                    setChatHistoryId={setChatHistoryId}
                    goBack={goBack}
                    group={group}
                  />
                )}
              </div>
            </AppModal>
          </>
        );
      }}
    />
  );
};

export default TutorReportModal;
